import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Spinner } from "@heroui/react";
import { Link } from "react-router-dom";
import { PencilIcon } from "@heroicons/react/24/solid";
import { K1DataTable } from "../components/K1DataTable";
import K1FilterBar from "../components/K1FilterBar";
import { useK1DashboardContext } from "../context/K1DashboardProvider";
import {
  buildFundScopeParams,
  buildK1DownloadUrl,
  fundSelectionKey,
  k1Api,
  type K1Fund,
  type K1FundSelection,
} from "../services/api";
import { DownloadButton } from "@/components/DownloadButton";

function buildPeFundsFilteredUrl(selection: K1FundSelection): string {
  const params = buildFundScopeParams(selection);
  return buildK1DownloadUrl("k1_pe_funds", params);
}

const textCell = (key: string): ColumnDef<K1Fund> => ({
  accessorKey: key,
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined) return "";
    return String(v);
  },
});

const boolCell = (key: string, header: string): ColumnDef<K1Fund> => ({
  accessorKey: key,
  header,
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined) return "";
    return v ? "Yes" : "No";
  },
});

const arrayCell = (key: string, header: string): ColumnDef<K1Fund> => ({
  accessorKey: key,
  header,
  cell: ({ getValue }) => {
    const v = getValue();
    if (!Array.isArray(v) || v.length === 0) return "";
    return v.join(", ");
  },
});

const pctCell = (key: string, header: string): ColumnDef<K1Fund> => ({
  accessorKey: key,
  header,
  meta: { className: "text-right" },
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined || v === "") return "";
    const n = parseFloat(String(v));
    if (isNaN(n)) return String(v);
    return (n * 100).toFixed(1) + "%";
  },
});

const nameCell: ColumnDef<K1Fund> = {
  accessorKey: "fund_name",
  header: "Fund Name",
  size: 200,
  cell: ({ getValue, row }) => {
    const v = getValue();
    return (
      <div className="flex items-center gap-1.5">
        <span className="truncate">{v != null ? String(v) : ""}</span>
        <Link
          to={String(row.original.id)}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 text-gray-400 hover:text-gray-700"
          title="Edit"
          aria-label="Edit"
        >
          <PencilIcon className="h-3.5 w-3.5" />
        </Link>
      </div>
    );
  },
};

const columns: ColumnDef<K1Fund>[] = [
  { ...nameCell, size: 200 },
  { ...textCell("k1_gp_id_fonds"), header: "GP ID Fonds", size: 120 },
  { ...arrayCell("old_names", "Mapping Name"), size: 160 },
  {
    ...textCell("name_of_general_partner"),
    header: "General Partner",
    size: 180,
  },
  { ...textCell("manager"), header: "Manager", size: 160 },
  { ...textCell("legal_form_fund"), header: "Legal Form", size: 120 },
  {
    ...textCell("fund_investment_state"),
    header: "Investment Status",
    size: 140,
  },
  { ...textCell("fund_domicile"), header: "Fund Domicile", size: 120 },
  { ...textCell("team_location_country"), header: "Team Location", size: 120 },
  {
    ...boolCell("fund_with_connection_to_germany", "Connection to Germany"),
    size: 80,
  } as ColumnDef<K1Fund>,
  { ...textCell("fund_currency"), header: "Currency", size: 80 },
  { ...textCell("commitment_date"), header: "Commitment Date", size: 120 },
  { ...textCell("signature_date"), header: "Signature Date", size: 120 },
  { ...textCell("liquidation_date"), header: "Liquidation Date", size: 120 },
  {
    accessorKey: "vintage_year",
    header: "Vintage Year",
    size: 90,
    cell: ({ getValue }) => getValue() ?? "",
  },
  { ...textCell("first_closing_date"), header: "First Closing", size: 110 },
  { ...textCell("final_closing_date"), header: "Final Closing", size: 110 },
  {
    ...textCell("first_investment_date_fund"),
    header: "First Investment",
    size: 120,
  },
  {
    ...textCell("end_of_investment_period"),
    header: "End of Inv. Period",
    size: 130,
  },
  { ...textCell("planned_end_date"), header: "Planned End", size: 110 },
  { ...textCell("generation"), header: "Generation", size: 90 },
  { ...textCell("sector_focus"), header: "Sector Focus", size: 140 },
  { ...textCell("strategy_focus"), header: "Strategy Focus", size: 140 },
  { ...textCell("geographic_focus"), header: "Geographic Focus", size: 140 },
  { ...textCell("asset_type_fund"), header: "Asset Type", size: 110 },
  { ...textCell("product_type"), header: "Product Type", size: 110 },
  { ...textCell("lei_code_fund"), header: "LEI Code", size: 160 },
  {
    ...textCell("in_which_closing_joined"),
    header: "Closing Joined",
    size: 120,
  },
  {
    ...pctCell("pct_of_private_investors", "% Private Investors"),
    size: 110,
  } as ColumnDef<K1Fund>,
  { ...textCell("gp_name"), header: "GP", size: 160 },
  { ...textCell("management_company_id"), header: "GP-ID", size: 80 },
  {
    ...boolCell("trr_required", "TRR required"),
    size: 100,
  } as ColumnDef<K1Fund>,
  { ...textCell("deadline_nb_days"), header: "Reporting deadline", size: 130 },
  {
    ...boolCell("first_production", "Included in 1st prod"),
    size: 120,
  } as ColumnDef<K1Fund>,
];

export default function PeFundsView() {
  const { selectedFundIds, fundSelection } = useK1DashboardContext();

  const { data, isLoading } = useQuery({
    queryKey: ["k1", "pe-funds-view", fundSelectionKey(fundSelection)],
    queryFn: () => k1Api.getFunds({ selection: fundSelection }),
    staleTime: 60_000,
    enabled: fundSelection.mode === "all" || selectedFundIds.length > 0,
  });

  const filteredRows = data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <K1FilterBar hidePeriod />
      {selectedFundIds.length === 0 ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      ) : isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading…" color="primary" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-1">
            <h1 className="font-semibold text-xl text-primary">PE Funds</h1>
            <div className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm">
              {filteredRows.length.toLocaleString()} funds
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
            <K1DataTable<K1Fund, unknown>
              table_name="k1-pe-funds"
              columns={columns}
              data={filteredRows}
              getRowClassName={() => "hover:!bg-slate-50"}
              pinnedLeft={["fund_name"]}
              noCard
            />
            <div className="flex items-center border-t border-slate-200 px-4 py-3">
              <DownloadButton
                href={buildPeFundsFilteredUrl(fundSelection)}
                filename={`k1_pe_funds_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`}
                size="sm"
                text="Download"
                className="text-sm"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
